<template>
  <!-- Discrimination Index -->
  <v-card outlined class="mb-6 pa-6">
    <!-- Header -->
    <v-row align="center" justify="space-between" class="mb-6">
      <v-col cols="12" md="6">
        <h2 class="text-h6 font-weight-bold">{{ $t("examAnalytics.discrimination") }}</h2>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end align-center">
        <v-select
          v-model="discriminationFilter"
          :items="[
            { text: $t('examAnalytics.All Questions'), value: 'all' },
            { text: $t('examAnalytics.Poor Questions'), value: 'poor' },
            { text: $t('examAnalytics.Good Questions'), value: 'good' },
            { text: $t('examAnalytics.Excellent Questions'), value: 'excellent' }
          ]"
          dense
          outlined
          hide-details
          style="max-width: 200px"
          class="mr-3"
        ></v-select>
      </v-col>
    </v-row>

    <!-- Legend -->
    <v-row class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <v-icon small color="green" class="mr-1">mdi-circle</v-icon>
        <span class="caption text--secondary">{{ $t("examAnalytics.Excellent") }} (≥ 0.3)</span>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-icon small color="orange" class="mr-1">mdi-circle</v-icon>
        <span class="caption text--secondary"
          >{{ $t("examAnalytics.Acceptable") }} (0.1 - 0.3)</span
        >
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-icon small color="red" class="mr-1">mdi-circle</v-icon>
        <span class="caption text--secondary">{{ $t("examAnalytics.Poor") }} (&lt; 0.1)</span>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card outlined>
      <v-simple-table dense>
        <thead>
          <tr>
            <th>{{ $t("examAnalytics.Question") }} #</th>
            <th class="text-center">{{ $t("examAnalytics.Correct (Top 27%)") }}</th>
            <th class="text-center">{{ $t("examAnalytics.Correct (Bottom 27%)") }}</th>
            <th class="text-center">{{ $t("examAnalytics.D-Index") }}</th>
            <th>{{ $t("examAnalytics.Verdict") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in paginatedQuestions"
            :key="`disc-${item.question}`"
            :class="getRowClass(item.dIndex)"
          >
            <td :title="item.questionTitle" class="text-body-2 font-weight-medium">
              {{ item.question }}
            </td>
            <td class="text-center">{{ item.topCorrect }} / {{ item.topTotal }}</td>
            <td class="text-center">{{ item.bottomCorrect }} / {{ item.bottomTotal }}</td>
            <td
              class="text-center font-weight-bold"
              :class="{
                'green--text': item.dIndex >= 0.3,
                'amber--text': item.dIndex >= 0.1 && item.dIndex < 0.3,
                'red--text': item.dIndex < 0.1
              }"
            >
              {{ formatSmartDecimal(item.dIndex) }}
            </td>
            <td class="font-weight-bold">
              <v-chip
                :color="
                  item.dIndex >= 0.3
                    ? 'green lighten-4'
                    : item.dIndex >= 0.1
                    ? 'amber lighten-4'
                    : 'red lighten-4'
                "
                :text-color="
                  item.dIndex >= 0.3
                    ? 'green darken-2'
                    : item.dIndex >= 0.1
                    ? 'amber darken-2'
                    : 'red darken-2'
                "
                small
                label
              >
                {{ $t("examAnalytics." + item.verdict) }}
              </v-chip>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <v-divider></v-divider>
      <v-row align="center" justify="space-between" class="ma-2">
        <!-- Student Count -->
        <v-col cols="12" md="4" class="text-start">
          <span class="grey--text font-weight-medium"
            >{{ filteredDiscriminationData().length }} {{ $t("examAnalytics.Question") }}</span
          >
        </v-col>

        <!-- Pagination Control -->
        <v-col cols="12" md="4" class="text-center">
          <v-pagination v-model="page" :length="pageCount" :total-visible="15" color="primary" />
        </v-col>

        <!-- Items Per Page -->
        <v-col cols="12" md="4" class="text-end">
          <v-select
            v-model="itemsPerPage"
            :items="[5, 10, 15, 25]"
            :label="$t('rowsPerPage')"
            dense
            outlined
            hide-details
            style="max-width: 160px; display: inline-block"
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Chart -->
    <v-row
      ><v-col cols="12" outlined class="mt-6">
        <Bar
          :key="discriminationChartData.datasets.length"
          :datasets="discriminationChartData.datasets"
          :labels="discriminationChartData.labels"
          :chart-options="discriminationChartOptions"
          class="mb-3"
          :height="150"
        ></Bar>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import Bar from "../../charts/Bar.vue";

export default {
  name: "Discrimination",
  components: { Bar },
  props: {
    ExamStatisticsData: {
      type: Object,
      required: true
    },
    Questions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      page: 1,
      itemsPerPage: 10,
      discriminationData: [],
      discriminationFilter: "all",
      discriminationChartData: {
        labels: [],
        datasets: []
      },
      discriminationChartOptions: {
        responsive: true,
        animation: true,
        plugins: {
          legend: { display: false },
          datalabels: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: ctx => `${this.$t("examAnalytics.D-Index")}: ${ctx.parsed.y.toFixed(2)}`
            }
          },
          datalabels: {
            color: this.$vuetify.theme.dark ? "white" : "#000",
            anchor: "end",
            align: "top",
            anchor: "end",
            textAlign: "center",
            formatter: value => value.toFixed(2), // Show only 2 decimals
            font: {
              size: 12
            }
          }
        },
        scales: {
          x: {

            title: { display: true, text: this.$t("examAnalytics.Question") + " #" },
            ticks: { color: "#666", autoSkip: false },
            grid: {
              color: this.$vuetify.theme.dark ? "#585858" : "#e0e0e0",
              borderDash: [1, 1]
            }
          },
          y: {
            title: { display: true, text: this.$t("examAnalytics.D-Index") },
            ticks: { color: "#666" },
            grid: {
              color: this.$vuetify.theme.dark ? "#585858" : "#e0e0e0",
              borderDash: [4, 4]
            }
          }
        }
      }
    };
  },
  created() {
    this.discriminationData = this.ExamStatisticsData.questions.map((item, index) => ({
      question: `${this.$t("examAnalytics.Q")} ${index + 1}`,
      questionTitle: this.Questions.filter(m => m.questionGuid == item.questionGuid)[0]
        .questionTitle,
      topCorrect: item.upperCorrect,
      topTotal: item.topTotal,
      bottomCorrect: item.lowerCorrect,
      bottomTotal: item.bottomTotal,
      dIndex: item.dIndex,
      verdict: item.verdict
    }));
  },
  mounted() {
    this.$nextTick(() => {
      this.computeDiscriminationChart();
    });
  },
  computed: {
    pageCount() {
      return Math.ceil(this.filteredDiscriminationData().length / this.itemsPerPage);
    },
    paginatedQuestions() {
      const start = (this.page - 1) * this.itemsPerPage;
      return this.filteredDiscriminationData().slice(start, start + this.itemsPerPage);
    },

    isDark() {
      return this.$vuetify.theme.dark;
    },
    getRowClass() {
      return dIndex => {
        if (dIndex >= 0.3) return this.isDark ? "" : "green lighten-5";
        if (dIndex >= 0.1) return this.isDark ? "" : "amber lighten-5";
        return this.isDark ? "" : "red lighten-5";
      };
    }
  },
  methods: {
    filteredDiscriminationData() {
      if (this.discriminationFilter === "all") {
        return this.discriminationData;
      } else if (this.discriminationFilter === "poor") {
        return this.discriminationData.filter(item => item.dIndex < 0.1);
      } else if (this.discriminationFilter === "good") {
        return this.discriminationData.filter(item => item.dIndex >= 0.1 && item.dIndex < 0.3);
      } else if (this.discriminationFilter === "excellent") {
        return this.discriminationData.filter(item => item.dIndex >= 0.3);
      }
      return this.discriminationData;
    },
    computeDiscriminationChart() {
      const questions = this.discriminationData.map(item => item.question);
      const dIndices = this.discriminationData.map(item => item.dIndex);
      const backgroundColors = this.discriminationData.map(item => {
        if (item.dIndex >= 0.3) return "#10B981";
        if (item.dIndex >= 0.1) return "#F59E0B";
        return "#EF4444";
      });

      this.discriminationChartData = {
        labels: questions,
        datasets: [
          {
            label: "D-Index",
            data: dIndices,
            backgroundColor: backgroundColors,
            barPercentage: 0.6,
            categoryPercentage: 0.6,
            borderRadius: 4
          }
        ]
      };
    }
  }
};
</script>
